import { KeyValueCustomSelect } from "@smartsuite/react-ui/lib";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import { CaptionBlock } from "../../../../../components/CaptionBlock";
import { useTheme } from "../../../../../stores";
import { useRootStore } from "../../../../../stores/hooks";

import {
  ActionLabel,
  Integrations,
  isSmartSuiteIntegration,
} from "../../../../../utils/constants";
import { getLocalCssColorVariables } from "../../../../../utils/cssVariablesUtils";
import * as A from "../../../../../stores/automation/mergers/actions";
import { RecordsNoOptions } from "./components/RecordsNoOptions/RecordsNoOptions";
import { RecordsOption } from "./components/RecordsOption/RecordsOption";
import { RecordsSelectedOption } from "./components/RecordsSelectedOption/RecordsSelectedOption";
import { OptionPreviewRow } from "./OptionPreviewRow";
import type { LoopSourcePreviewTarget } from "../../../../../stores/flow-canvas.store";
import * as S from "./style";
import type { RecordsSourceOption } from "./utils";
import {
  getAddNewOption,
  isLoopingOutputAction,
  isLoopingOutputTrigger,
  webhookActionsToLoopingOptions,
  webhookTriggerToLoopingOptions,
} from "./utils";
import { observer } from "mobx-react-lite";
import { useUpdateJSONEditorDescription } from "../../../../../hooks/use-update-json-editor-description";
import { t } from "i18next";
import { getSourceDataFromOption } from "./util/get-source-data-from-option";
import { getOptionFromSource } from "./util/get-option-from-source";
import type { ActionCandidate } from "../../../../../types/candidate";
import type { CandidateStore } from "../../../../../stores/automation/candidate.store";
import type { LoopingStore } from "../../../../../stores/automation/looping.store";

export const SelectFindRecords: React.FC<{ loopInstanceId?: number }> =
  observer(({ loopInstanceId }) => {
    const store = useRootStore();
    const { candidate } = store.automations;
    const looping = store.automations.candidate.looping;

    const theme = useTheme();
    const [options, setOptions] = useState<RecordsSourceOption[]>([]);
    const [showNoOptions, setShowNoOptions] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const [value, setValue] = useState<string | undefined>(() =>
      getOptionFromSource(
        loopInstanceId !== undefined
          ? // getOptionFromSource only reads `.source` off the store it's
            // given -- a minimal stand-in avoids needing a real scoped
            // LoopingStore just for this one read (precedent:
            // looping.store.ts's own outputReferenceToOptionValue call).
            ({
              source: looping.sourceAt(loopInstanceId),
            } as unknown as LoopingStore)
          : candidate.looping,
        candidate.allActions,
      ),
    );

    useUpdateJSONEditorDescription(
      [Integrations.webhooks, Integrations.ai],
      "",
    );

    // Base candidate list differs by context: a nested loop (loopInstanceId
    // set) may only offer find-records/looping-output actions that execute
    // BEFORE it on its own ancestor path (root actions + each ancestor
    // branch's/loop's own direct actions) -- sibling branches and anything
    // nested inside the loop itself are not valid sources. The root
    // Looping singleton keeps its original root-scoped list unchanged.
    const findRecordsActionsBase =
      loopInstanceId !== undefined
        ? candidate.branches.upstreamActionsOf(loopInstanceId)
        : candidate.actionsFromGroup;

    // upstreamActionsOf is a store METHOD (not a computed), so it returns a
    // fresh array identity on every render -- memoizing on the array itself
    // would recreate findRecordsActions each render, and the
    // useEffect([findRecordsActions]) below would then setOptions in a
    // render loop. Key the memo on a content fingerprint instead, so the
    // reference stays stable until the actual candidate set changes (the
    // singleton path's actionsFromGroup computed gets the same treatment
    // harmlessly). `actionName` is included alongside hash/instanceId
    // because getAllOptions() (via webhookActionsToLoopingOptions) renders
    // the option label from the action's action-name input, and renaming an
    // action through the FieldEditor mirrors that input onto
    // `action.actionName` in the same edit (see mergeActionName) -- without
    // it here, an in-place rename of an upstream action while this panel is
    // mounted would leave the option label stale.
    const findRecordsActionsKey = findRecordsActionsBase
      .map(
        (action) =>
          `${action.hash}:${action.actionReference?.instanceId ?? ""}:${
            action.actionName ?? ""
          }`,
      )
      .join("|");

    const findRecordsActions = useMemo(
      () =>
        findRecordsActionsBase.filter((action) => {
          if (action.actionReference?.actionId === ActionLabel.findRecords) {
            return true;
          }

          if (isLoopingOutputAction(action)) {
            return true;
          }

          return false;
        }),
      [findRecordsActionsKey, loopInstanceId],
    );

    const integrations = store.integrations.list;

    useEffect(() => {
      const allOptions = getAllOptions(findRecordsActions, candidate);

      if (allOptions.length === 0) {
        setValue(undefined);
        setShowNoOptions(true);
        return;
      }

      setShowNoOptions(false);
      allOptions.push(getAddNewOption());
      setOptions(allOptions);
    }, [findRecordsActions]);

    // Whether hover previews are currently allowed. A ref, not the `open`
    // state: the rows read it through one stable callback, and it must flip
    // synchronously inside onChange, before the closing animation starts.
    const previewsEnabledRef = useRef(false);

    const setLoopSourcePreview = useCallback(
      (
        next:
          | { kind: "trigger" }
          | { kind: "action"; actionHash: string }
          | undefined,
      ): void => {
        // Only a LIVE list may set a preview. Once it starts closing, the
        // collapsing rows slide under the stationary pointer and Chrome
        // re-fires mouseenter on whichever row lands there, flashing its
        // canvas card until the row unmounts (STR-14148). Clears always pass.
        if (next !== undefined && !previewsEnabledRef.current) {
          return;
        }
        store.flowCanvas.setLoopSourcePreviewTarget(next);
      },
      [store.flowCanvas],
    );

    const onOpenedChange = (e: boolean): void => {
      previewsEnabledRef.current = e;
      setOpen(e);
      if (!e) setLoopSourcePreview(undefined);
    };

    useEffect(
      () => () => setLoopSourcePreview(undefined),
      [setLoopSourcePreview],
    );

    const getAllOptions = (
      findRecordsActions: ActionCandidate[],
      candidate: CandidateStore,
    ): RecordsSourceOption[] => {
      const actionOptions = webhookActionsToLoopingOptions(findRecordsActions);
      const triggerOptions = isLoopingOutputTrigger(candidate.self.trigger)
        ? webhookTriggerToLoopingOptions(candidate.self.trigger)
        : [];

      return [...triggerOptions, ...actionOptions];
    };

    const createNewFindAction = (): void => {
      const hash = uuid();
      const ssIntegrations = integrations.filter((integration) =>
        isSmartSuiteIntegration(integration.integrationId),
      );

      const integration = ssIntegrations.find((integration) =>
        integration.actions.find(
          (action) => action.actionId === ActionLabel.findRecords,
        ),
      );

      const integrationId = integration?.integrationId;

      if (integrationId) {
        candidate.setChangeActiveActionTo(hash);

        candidate.setActions(
          [
            A.mergeEmptyAction(
              hash,
              integration?.integrationId,
              ActionLabel.findRecords,
            ),
          ],
          "actions",
        );
      }
    };

    const renderOption = (
      option: RecordsSourceOption,
      active: boolean,
    ): React.ReactNode => {
      const actionHash =
        option.type === "action" &&
        option.actionReference?.instanceId !== undefined
          ? candidate.allActions.find(
              (a) =>
                a.actionReference?.instanceId ===
                option.actionReference?.instanceId,
            )?.hash
          : undefined;

      const previewTarget: LoopSourcePreviewTarget | undefined =
        option.type === "trigger"
          ? { kind: "trigger" }
          : actionHash !== undefined
            ? { kind: "action", actionHash }
            : undefined;

      return (
        <OptionPreviewRow
          target={previewTarget}
          onPreview={setLoopSourcePreview}
        >
          <RecordsOption option={option} active={active} />
        </OptionPreviewRow>
      );
    };

    const renderSelectedOption = (
      option: RecordsSourceOption,
    ): React.ReactNode => {
      return <RecordsSelectedOption option={option} />;
    };

    return (
      <S.RecordsSourceEditorWrapper>
        <S.GlobalStyle />
        <CaptionBlock
          description={{
            label: t("looping.select-list"),
            subLabel: t("looping.select-list-description"),
            icon: "",
            dynamic: false,
          }}
        />
        {showNoOptions && (
          <RecordsNoOptions createNewFindAction={createNewFindAction} />
        )}
        {!showNoOptions && (
          <KeyValueCustomSelect
            my="left top+2"
            state={open ? "active" : "default"}
            showClear={false}
            placeholderArrowStyle="filled-dark"
            className={
              open
                ? "container-is-open records-source-selector records-source-selector_looping"
                : "records-source-selector records-source-selector_looping"
            }
            portalStyles={getLocalCssColorVariables(theme)}
            options={options}
            onOpenedChange={onOpenedChange}
            onChange={(value) => {
              // Selecting closes the list: stop accepting hover previews from
              // its collapsing rows BEFORE clearing, then clear the one the
              // selection-click's row left behind (its onMouseLeave never
              // fires -- the row unmounts) (STR-14148).
              previewsEnabledRef.current = false;
              setLoopSourcePreview(undefined);
              switch (value) {
                case "no-actions": {
                  return;
                }
                case "add-new-find": {
                  createNewFindAction();
                  return;
                }
              }
              setValue(String(value));
              const option = options.find((option) => option.value === value);
              if (option) {
                const sourceData = getSourceDataFromOption(
                  option,
                  candidate.self.trigger,
                  candidate.allActions,
                );

                if (sourceData) {
                  if (loopInstanceId !== undefined) {
                    looping.setSourceAt(loopInstanceId, sourceData);
                  } else {
                    looping.setSource(sourceData);
                  }
                }
              }
            }}
            renderOption={renderOption}
            renderSelectedOption={renderSelectedOption}
            value={value}
            listItemsClassname="records-source-selector-list-items"
          />
        )}
      </S.RecordsSourceEditorWrapper>
    );
  });
